import React from 'react';

/**
 * Sostituto di `<input type="number">` che:
 * - impedisce i valori negativi già in digitazione (`min` vale solo al submit);
 * - limita i decimali a `maxDecimals` (default 2; 0 = interi, Infinity = nessun limite).
 * Blocca i tasti `-`, `+`, `e`, `E` e il paste con `-`.
 */
type NumberInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> & {
    maxDecimals?: number;
};

const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
    ({ onKeyDown, onPaste, onChange, maxDecimals = 2, ...rest }, ref) => {
        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (['-', '+', 'e', 'E'].includes(e.key)) {
                e.preventDefault();
            } else if (e.key === '.' && !e.ctrlKey && !e.metaKey && !e.altKey) {
                // "7." è un valore non valido per il campo numerico (value vuoto): chi usa il campo
                // lo leggeva come 0 e la cifra successiva dava "05". "7," invece resta 7, quindi il
                // punto si scrive come virgola; il valore finale è lo stesso (7,5 = 7.5).
                e.preventDefault();
                if (typeof document.execCommand === 'function') document.execCommand('insertText', false, ',');
            }
            onKeyDown?.(e);
        };

        const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
            const pasted = e.clipboardData.getData('text');
            if (pasted.includes('-')) {
                e.preventDefault();
            }
            onPaste?.(e);
        };

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            if (Number.isFinite(maxDecimals) && maxDecimals >= 0) {
                const val = e.target.value;
                const dot = val.indexOf('.');
                if (dot >= 0) {
                    const decimals = val.length - dot - 1;
                    if (decimals > maxDecimals) {
                        e.target.value = maxDecimals === 0
                            ? val.slice(0, dot)
                            : val.slice(0, dot + 1 + maxDecimals);
                    }
                }
            }
            onChange?.(e);
        };

        return (
            <input
                ref={ref}
                type="number"
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onChange={handleChange}
                {...rest}
            />
        );
    }
);
NumberInput.displayName = 'NumberInput';

export default NumberInput;
